import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';

import { NotificationService, NotificationSettings } from '@progress/kendo-angular-notification';
import { Observable, exhaustMap, switchMap, tap } from 'rxjs';
import { Employee, IEmployee, IEmployeeCreateRequest, IEmployeeRequest } from 'src/app/employee/employee';
import { EmployeeService } from 'src/app/employee/services/employee.service';
import { TimeSpan } from 'src/app/shared/helpers/time-span';


export interface EmployeeState {
  employee: IEmployee;
  isLoading: boolean;
}

@Injectable()
export class EmployeeStore extends ComponentStore<EmployeeState> {
  constructor(
    private employeeService: EmployeeService,
    private notification: NotificationService,
    private translocoService: TranslocoService,
    private router: Router,
    // private location: Location,
    ) {
    super({
      employee: null,
      isLoading: false,
    });
  }

  readonly employee$ = this.select(({ employee }) => employee);

  readonly isLoading$ = this.select(({ isLoading }) => isLoading);


  setIsLoading = this.updater((state) => ({ ...state, isLoading: true }));

  setError = this.updater((state, error: HttpErrorResponse) => ({
    ...state,
    isLoading: false,
    error: error.message,
  }));


  readonly setEmployee = this.updater((state, employee: IEmployee) => ({
    ...state,
    isLoading: false,
    errorData: null,
    employee: this.convertFiscalYearReportTimeSpan(employee)
  }));



  getEmployee = this.effect((getEmployeeRequest$: Observable<string>) => {
    return getEmployeeRequest$.pipe(
      tap(() => {
        this.setIsLoading();
      }),
      exhaustMap((request) => {
        return this.employeeService.getEmployee(request).pipe(
          tapResponse(
            (employee) => {
              this.setEmployee(employee);
            },
            (err: HttpErrorResponse) => this.setError(err)
          )
        );
      })
    );
  });

  createEmployee = this.effect((createEmployee$: Observable<IEmployeeCreateRequest>) => {
    return createEmployee$.pipe(
      tap(() => {
        this.setIsLoading();
      }),
      switchMap((request) => {
        return this.employeeService.createEmployee(request)
          .pipe(
            tapResponse(
              (response) => {
                    this.setEmployee(response.employee);
                    const settings: NotificationSettings = {
                      content: this.translocoService.translate('employee.saveSuccess'),
                      hideAfter: 4000,
                      cssClass: "notification",
                      position: { horizontal: "center", vertical: "top" },
                      animation: { type: "fade", duration: 250 },
                      type: { style: "success", icon: true },
                    };
                
                    this.notification.show(settings);
                    const newRoute = `${this.router.url}/${response.employee.id}`;
                    this.router.navigate([newRoute]);
              },
              (err: HttpErrorResponse) => this.setError(err)
            )
        );
      })
    );
  });

  updateEmployee = this.effect((updateEmployee$: Observable<IEmployeeRequest>) => {
    return updateEmployee$.pipe(
      tap(() => {
        this.setIsLoading();
      }),
      switchMap((request) => {
        return this.employeeService.updateEmployee(request)
          .pipe(
            tapResponse(
              (response) => {
                    this.setEmployee(response.employee);
                    const settings: NotificationSettings = {
                      content: this.translocoService.translate('employee.saveSuccess'),
                      hideAfter: 4000,
                      cssClass: "notification",
                      position: { horizontal: "center", vertical: "top" },
                      animation: { type: "fade", duration: 250 },
                      type: { style: "success", icon: true },
                    };
                
                    this.notification.show(settings);
              },
              (err: HttpErrorResponse) => this.setError(err)
            )
        );
      })
    );
  });


  deleteEmployee = this.effect((deleteEmployee$: Observable<string>) => {
    return deleteEmployee$.pipe(
      tap(() => {
        this.setIsLoading();
      }),
      exhaustMap((positionId) => {
        return this.employeeService.deleteEmployee(positionId).pipe(
          tapResponse(
            (response) => {
              const settings: NotificationSettings = {
                content: this.translocoService.translate('vehicle.deleteSuccess'),
                hideAfter: 4000,
                cssClass: "notification",
                position: { horizontal: "center", vertical: "top" },
                animation: { type: "fade", duration: 250 },
                type: { style: "success", icon: true },
              };
          
              this.notification.show(settings);
              this.router.navigate(['/settings/employees'])
            },
            (err: HttpErrorResponse) => this.setError(err)
          )
        );
      })
    );
  });

  // Helper Functions

  private convertFiscalYearReportTimeSpan(employee: IEmployee){
    if(employee?.fiscalYearEmployeeReports.length > 0){
      employee.fiscalYearEmployeeReports = employee?.fiscalYearEmployeeReports?.map((fiscalYearReport) => {
        return {
          ...fiscalYearReport,
          flexTimeBalanceObj: TimeSpan.parse(fiscalYearReport.flexTimeBalance).getAsTimeObject(),
          preWorkTimeBalanceObj: TimeSpan.parse(fiscalYearReport.preWorkTimeBalance).getAsTimeObject(),
          overtimeBalanceObj: TimeSpan.parse(fiscalYearReport.overtimeBalance).getAsTimeObject(),
        }
      })
    }

    return employee
  }

}
